import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { CustomerGroup, CreateCustomerGroupData, UpdateCustomerGroupData } from "../../../types/customerGroup";
import { Customer, CreateCustomerData } from "../../../services/customerService";
import customerGroupService from "../../../services/customerGroupService";
import customerService from "../../../services/customerService";
import CustomerMultiSelect from "../components/CustomerMultiSelect";
import ErrorAlert from "../../../shared/components/ui/ErrorAlert";
import Button from "../../../shared/components/ui/button/Button";
import RightDrawer from "../../../shared/components/form/RightDrawer";
import CustomerForm from "../../customer/components/CustomerForm";

interface CustomerGroupFormProps {
  editingItem: CustomerGroup | null;
  onAdd: (item: CustomerGroup) => void;
  onClose: () => void;
}

const schema = yup.object({
  customerGroupName: yup
    .string()
    .required("Customer group name is required")
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name must be less than 100 characters"),
  customerId: yup
    .array()
    .of(yup.string())
    .min(1, "At least one customer must be selected")
    .required("At least one customer must be selected"),
  status: yup
    .string()
    .oneOf(['active', 'inactive'] as const)
    .notRequired()
    .default('active'),
});

export default function CustomerGroupForm({ 
  editingItem, 
  onAdd, 
  onClose 
}: CustomerGroupFormProps) {
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);
  const [openCustomerForm, setOpenCustomerForm] = useState<boolean>(false);
  
  const { 
    register, 
    handleSubmit, 
    reset,
    watch,
    setValue,
    trigger,
    formState: { errors, isDirty, isValid },
    setFocus
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      customerGroupName: "",
      customerId: [],
      status: "active",
    },
    mode: "onChange"
  });

  const watchedName = watch("customerGroupName");

  // Fetch customers on component mount and when customer form closes
  const fetchCustomers = async () => {
    setIsLoadingCustomers(true);
    try {
      // Fetch all active customers without pagination (no limit/page params)
      const response = await customerService.getCustomers({
        status: 'active',
      });
      // Handle both paginated and non-paginated responses
      setCustomers(response.data || []);
    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomers([]);
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  // Set form values when editing
  useEffect(() => {
    if (editingItem) {
      const customerIds = editingItem.customerId || [];
      setSelectedCustomerIds(Array.isArray(customerIds) ? customerIds : []);
      reset({
        customerGroupName: editingItem.customerGroupName,
        customerId: Array.isArray(customerIds) ? customerIds : [],
        status: editingItem.status,
      });
    } else {
      setSelectedCustomerIds([]);
      reset();
    }
    setFocus("customerGroupName");
  }, [editingItem, reset, setFocus]);

  // Update form value when selectedCustomerIds changes
  useEffect(() => {
    setValue("customerId", selectedCustomerIds, { shouldDirty: true, shouldValidate: true });
    // Manually trigger validation to ensure form state is updated
    trigger("customerId");
  }, [selectedCustomerIds, setValue, trigger]);

  const onSubmit = async (data: { customerGroupName: string; customerId: string[]; status: 'active' | 'inactive' }) => {
    setIsSubmitting(true);
    setApiError(null);
    setShowSuccess(false);
    
    try {
      let res: CustomerGroup;
      const payload: CreateCustomerGroupData | UpdateCustomerGroupData = {
        customerGroupName: data.customerGroupName.trim(),
        status: "active",
        customerId: selectedCustomerIds.length > 0 ? selectedCustomerIds : [],
      };
      
      if (editingItem) {
        res = await customerGroupService.updateCustomerGroup(editingItem._id, payload as UpdateCustomerGroupData);
      } else {
        res = await customerGroupService.createCustomerGroup(payload as CreateCustomerGroupData);
      }
      
      setShowSuccess(true);
      setTimeout(() => {
        onAdd(res);
        onClose();
      }, 1000);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setApiError(error.response?.data?.message || "An error occurred while saving the customer group");
      console.error("Form submission error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {showSuccess && (
        <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-800">
              {editingItem ? "Customer group updated successfully!" : "Customer group created successfully!"}
            </p>
          </div>
        </div>
      )}
      
      {apiError && (
        <ErrorAlert 
          message={apiError} 
          onClose={() => setApiError(null)} 
          className="mb-4"
        />
      )}
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Customer Group Name Field */}
        <div className="space-y-2">
          <label 
            htmlFor="customerGroupName" 
            className="block text-sm font-semibold text-gray-700 mb-2"
          >
            Customer Group Name *
          </label>
          <div className="relative">
            <input
              id="customerGroupName"
              type="text"
              {...register("customerGroupName")}
              className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${
                errors.customerGroupName
                  ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                  : watchedName && !errors.customerGroupName
                  ? "border-green-300 focus:border-green-500 focus:ring-green-500/20"
                  : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
              }`}
              placeholder="Enter customer group name"
            />
            {watchedName && !errors.customerGroupName && (
              <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-500" />
            )}
            {errors.customerGroupName && (
              <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500" />
            )}
          </div>
          {errors.customerGroupName && (
            <p className="flex items-center gap-1 text-sm text-red-600">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {errors.customerGroupName.message}
            </p>
          )}
          {watchedName && !errors.customerGroupName && (
            <p className="flex items-center gap-1 text-sm text-green-600">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              Name looks good!
            </p>
          )}
        </div>
        
        {/* Customer ID Multi-Select Field */}
        <CustomerMultiSelect
          label="Select Customers"
          customers={customers}
          selectedCustomerIds={selectedCustomerIds}
          onChange={setSelectedCustomerIds}
          isLoading={isLoadingCustomers}
          error={errors.customerId?.message}
          onAddNewCustomer={() => setOpenCustomerForm(true)}
        />
        
        {/* Action Buttons - Sticky Footer */}
        <div className="sticky bottom-0 flex flex-col sm:flex-row justify-end gap-3 pt-5 pb-2 border-t border-gray-200 mt-6 bg-white z-10">
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto order-2 sm:order-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            disabled={isSubmitting || !isDirty || !isValid}
            className="w-full sm:w-auto min-w-[140px] order-1 sm:order-2"
          >
            {isSubmitting ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {editingItem ? "Updating..." : "Creating..."}
              </div>
            ) : editingItem ? (
              "Update Customer Group"
            ) : (
              "Create Customer Group"
            )}
          </Button>
        </div>
      </form>

      {/* Customer Form Drawer */}
      <RightDrawer
        open={openCustomerForm}
        title="Add New Customer"
        onClose={() => setOpenCustomerForm(false)}
        size="60"
      >
        <div className="p-1">
          <CustomerForm
            editingCustomer={null}
            onAdd={async (customerData: CreateCustomerData) => {
              try {
                const response = await customerService.createCustomer(customerData);
                if (response.data && response.data._id) {
                  // Refresh customers list
                  await fetchCustomers();
                  // Auto-select the newly created customer
                  setSelectedCustomerIds(prev => [...prev, response.data._id]);
                  // Close the customer form drawer
                  setOpenCustomerForm(false);
                }
              } catch (error: any) {
                console.error("Failed to create customer:", error);
                throw error; // Re-throw to let CustomerForm handle the error display
              }
            }}
            onClose={() => setOpenCustomerForm(false)}
            showSuccessAndClose={false}
          />
        </div>
      </RightDrawer>
    </div>
  );
}
